import { useState, useEffect, useRef, useMemo } from 'react';
import { Loader2, Check, ChevronsUpDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveStatusId } from '@/hooks/useDefaultStatus';
import { profileService, vendorService, priceBookService, invoiceService } from '@/services';
import { newInvoiceDialogSchema, type NewInvoiceDialogForm } from '@prolarus/shared';
import { resolvePaymentTerms, PaymentTermsParseError } from '@prolarus/shared';

interface NewInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (invoiceId: string) => void;
}

function getTodayString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const defaultValues: NewInvoiceDialogForm = {
  vendor_id: '',
  invoice_number: '',
  invoice_date: getTodayString(),
  service_date: '',
  due_date: '',
  po_number: '',
  payment_terms: '',
  currency: 'USD',
  notes: '',
  discount_percent: '',
  discount_expiration: '',
  price_book_id: null,
};

export default function NewInvoiceDialog({ open, onOpenChange, onSave }: NewInvoiceDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);
  const [priceBookPopoverOpen, setPriceBookPopoverOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const skipNextVendorClear = useRef(false);
  const [flashFields, setFlashFields] = useState<Record<string, boolean>>({});
  const flashTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const flashField = (fieldName: string) => {
    if (flashTimers.current[fieldName]) clearTimeout(flashTimers.current[fieldName]);
    setFlashFields(prev => ({ ...prev, [fieldName]: true }));
    flashTimers.current[fieldName] = setTimeout(() => {
      setFlashFields(prev => ({ ...prev, [fieldName]: false }));
    }, 1500);
  };

  const form = useForm<NewInvoiceDialogForm>({
    resolver: zodResolver(newInvoiceDialogSchema),
    defaultValues,
  });

  // Fetch user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.getCurrentProfile();
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Get active vendor status for filtering
  const { data: activeVendorStatus } = useActiveStatusId('vendor');

  // Fetch active vendors
  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-active', profile?.company_id, activeVendorStatus?.id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await vendorService.listActive(profile.company_id, activeVendorStatus?.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  // Fetch price books
  const { data: priceBooks = [] } = useQuery({
    queryKey: ['price-books-selector', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await priceBookService.listForSelector(profile.company_id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!profile?.company_id,
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({ ...defaultValues, invoice_date: getTodayString() });
      setFlashFields({});
    }
  }, [open, form]);

  const watchedVendorId = form.watch('vendor_id');
  const watchedPriceBookId = form.watch('price_book_id');

  // Effect: price book selected -> auto-set vendor
  useEffect(() => {
    if (!watchedPriceBookId) return;
    const pb = priceBooks.find(p => p.id === watchedPriceBookId);
    if (pb && pb.vendor_id && pb.vendor_id !== watchedVendorId) {
      skipNextVendorClear.current = true;
      form.setValue('vendor_id', pb.vendor_id, { shouldValidate: true });
    }
  }, [watchedPriceBookId, priceBooks, form]);

  // Effect: vendor changed -> clear mismatched price book
  useEffect(() => {
    if (skipNextVendorClear.current) {
      skipNextVendorClear.current = false;
      return;
    }
    if (!watchedVendorId || !watchedPriceBookId) return;
    const pb = priceBooks.find(p => p.id === watchedPriceBookId);
    if (pb && pb.vendor_id !== watchedVendorId) {
      form.setValue('price_book_id', null);
    }
  }, [watchedVendorId]);

  // Filtered price books based on selected vendor
  const filteredPriceBooks = useMemo(() => {
    if (!watchedVendorId) return priceBooks;
    return priceBooks.filter(pb => pb.vendor_id === watchedVendorId);
  }, [watchedVendorId, priceBooks]);

  // Resolve payment terms and auto-populate dependent fields with flash
  const applyPaymentTerms = (overrideInvoiceDate?: string) => {
    const paymentTerms = form.getValues('payment_terms');
    const invoiceDate = overrideInvoiceDate ?? form.getValues('invoice_date');
    if (!paymentTerms || !invoiceDate) return;

    try {
      const result = resolvePaymentTerms(paymentTerms, new Date(invoiceDate));
      const newDueDate = formatDateString(result.dueDate);
      const newDiscountPercent: number | '' = result.discountPercent || '';
      const newDiscountExpiration = result.discountPercent
        ? formatDateString(result.discountExpirationDate)
        : '';

      if (newDueDate !== (form.getValues('due_date') || '')) {
        form.setValue('due_date', newDueDate);
        flashField('due_date');
      }
      if (String(newDiscountPercent) !== String(form.getValues('discount_percent') ?? '')) {
        form.setValue('discount_percent', newDiscountPercent);
        flashField('discount_percent');
      }
      if (newDiscountExpiration !== (form.getValues('discount_expiration') || '')) {
        form.setValue('discount_expiration', newDiscountExpiration);
        flashField('discount_expiration');
      }
    } catch (e) {
      if (e instanceof PaymentTermsParseError) return;
      throw e;
    }
  };

  const handleSubmit = async (data: NewInvoiceDialogForm) => {
    if (!profile?.company_id || !user?.id) return;

    setIsLoading(true);
    try {
      const insertData = {
        vendor_id: data.vendor_id,
        invoice_number: data.invoice_number,
        invoice_date: data.invoice_date,
        service_date: data.service_date || null,
        due_date: data.due_date || null,
        po_number: data.po_number || null,
        payment_terms: data.payment_terms || null,
        currency: data.currency || 'USD',
        notes: data.notes || null,
        price_book_id: data.price_book_id || null,
        discount: data.discount_percent ? Number(data.discount_percent) : null,
        discount_expiration: data.discount_expiration || null,
        created_by: user.id,
        updated_by: user.id,
      };

      const result = await invoiceService.create(insertData, {
        companyId: profile.company_id,
        userId: user.id,
      });

      if (result.error) {
        toast({
          title: 'Error',
          description: 'Failed to create invoice',
          variant: 'destructive',
        });
        return;
      }

      toast({ title: 'Success', description: 'Invoice created successfully' });
      onOpenChange(false);
      onSave(result.data!.id);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                form.handleSubmit(handleSubmit)();
              }
            }}
            className="space-y-4"
          >
            {/* Row 1: Vendor (full width) */}
            <FormField
              control={form.control}
              name="vendor_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Vendor *</FormLabel>
                  <Popover open={vendorPopoverOpen} onOpenChange={setVendorPopoverOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" role="combobox" className="w-full justify-between">
                          <span className="truncate">
                            {field.value
                              ? vendors.find(v => v.id === field.value)?.name || 'Select vendor...'
                              : 'Select vendor...'}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search vendors..." />
                        <CommandList>
                          <CommandEmpty>No vendor found.</CommandEmpty>
                          <CommandGroup>
                            {vendors.map((vendor) => (
                              <CommandItem
                                key={vendor.id}
                                value={vendor.name}
                                onSelect={() => {
                                  field.onChange(vendor.id);
                                  setVendorPopoverOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", field.value === vendor.id ? "opacity-100" : "opacity-0")} />
                                {vendor.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Row 2: Price Book (full width) */}
            <FormField
              control={form.control}
              name="price_book_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Price Book</FormLabel>
                  <Popover open={priceBookPopoverOpen} onOpenChange={setPriceBookPopoverOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" role="combobox" className="w-full justify-between">
                          <span className="truncate">
                            {field.value
                              ? (() => {
                                  const pb = priceBooks.find(p => p.id === field.value);
                                  return pb ? `${pb.name}${pb.vendor?.name ? ` \u2014 ${pb.vendor.name}` : ''}` : 'Select price book...';
                                })()
                              : 'Select price book...'}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search price books..." />
                        <CommandList>
                          <CommandEmpty>No price book found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="__none__"
                              onSelect={() => {
                                field.onChange(null);
                                setPriceBookPopoverOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", !field.value ? "opacity-100" : "opacity-0")} />
                              None
                            </CommandItem>
                            {filteredPriceBooks.map((pb) => (
                              <CommandItem
                                key={pb.id}
                                value={`${pb.name} ${pb.vendor?.name || ''}`}
                                onSelect={() => {
                                  field.onChange(pb.id);
                                  setPriceBookPopoverOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", field.value === pb.id ? "opacity-100" : "opacity-0")} />
                                {pb.name}{pb.vendor?.name ? ` \u2014 ${pb.vendor.name}` : ''}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Row 3: Invoice Number | PO Number */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="invoice_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Number *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="po_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PO Number</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 4: Invoice Date | Service Date */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="invoice_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Date *</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          if (form.getValues('payment_terms')) {
                            applyPaymentTerms(e.target.value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="service_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value || '')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 5: Payment Terms (full width) */}
            <FormField
              control={form.control}
              name="payment_terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Terms</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ''}
                      onBlur={() => {
                        field.onBlur();
                        applyPaymentTerms();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Row 6: Due Date | Discount % */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ''}
                        className={cn("transition-colors duration-700", flashFields.due_date && "bg-green-100 dark:bg-green-950/40")}
                        onChange={(e) => field.onChange(e.target.value || '')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="discount_percent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discount %</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="any"
                        {...field}
                        value={field.value ?? ''}
                        className={cn("transition-colors duration-700", flashFields.discount_percent && "bg-green-100 dark:bg-green-950/40")}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val === '' ? '' : Number(val));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 7: Discount Expires On | Currency */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="discount_expiration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discount Expires On</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ''}
                        className={cn("transition-colors duration-700", flashFields.discount_expiration && "bg-green-100 dark:bg-green-950/40")}
                        onChange={(e) => field.onChange(e.target.value || '')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 8: Notes (full width) */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value || ''} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create & Edit'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
